import { useState } from "react";
import {
  Printer,
  Edit,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Clock,
  ChevronRight,
  ShieldCheck,
  Thermometer,
  Layers,
  ArrowRight,
  Activity,
  History,
  FileCheck,
  UserCheck,
  Warehouse,
  Check,
} from "lucide-react";
import {
  Badge,
  Button,
  DetailField,
  DetailFieldGrid,
  DetailSectionCard,
  RelatedEntityTable,
  StatusBadge,
  StatusStepper,
  Tabs,
} from "@/shared/ui";
import { Breadcrumb } from "@/shared/ui/layout/Breadcrumb";
import { cn } from "@/shared/lib/cn";
import { MOCK_SCENARIOS, type TestUiScenario, type TestUiLineItem } from "../model/testUiData";

export interface TestUiDetailSectionProps {
  scenarioId: string;
  onEditClick: () => void;
}

export function TestUiDetailSection({
  scenarioId,
  onEditClick,
}: TestUiDetailSectionProps) {
  const scenario: TestUiScenario = MOCK_SCENARIOS[scenarioId] ?? MOCK_SCENARIOS.mes!;
  const [activeTab, setActiveTab] = useState<"overview" | "lines" | "ccp" | "docs" | "audit">("overview");

  const tabs = [
    { id: "overview", label: "Tổng quan điều hành" },
    { id: "lines", label: `Dòng hàng & Định mức (${scenario.lines.length})` },
    { id: "ccp", label: "Kiểm soát CCP & Nhiệt độ" },
    { id: "docs", label: "Tài liệu & Hồ sơ lô" },
    { id: "audit", label: "Nhật ký kiểm toán" },
  ];

  return (
    <section className="space-y-4">
      {/* ========================================================================= */}
      {/* TOP BAR: BREADCRUMB & PAGE ACTIONS (Cùng dòng ngoài section card)          */}
      {/* ========================================================================= */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Breadcrumb
          items={[
            { label: "Hệ thống Ops", to: "/testui" },
            { label: scenario.category, to: "/testui" },
            { label: scenario.code },
          ]}
          className="min-w-0"
        />

        {/* Action CTAs using core Button */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Printer className="size-3.5 text-text-muted" />}
          >
            In lệnh SX
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={onEditClick}
            leftIcon={<Edit className="size-3.5 text-text-muted" />}
          >
            Chỉnh sửa
          </Button>
          <Button
            size="sm"
            variant="primary"
            leftIcon={<CheckCircle2 className="size-3.5" />}
          >
            Duyệt & Chuyển công đoạn
          </Button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TIER 1: IDENTITY & HEADER (Mã phiếu duy nhất, Status)                     */}
      {/* ========================================================================= */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        {/* Title row */}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="m-0 font-mono text-h1 font-bold text-text">
              {scenario.code}
            </h2>
            <StatusBadge status={scenario.status} />
            <Badge
              tone={scenario.priority === "Khẩn cấp" ? "danger" : "warning"}
              label={scenario.priority}
            />
            <span className="rounded-badge bg-bg border border-border px-2 py-0.5 text-caption text-text-secondary">
              {scenario.plant}
            </span>
          </div>
          <p className="m-0 text-body font-medium text-text-secondary">
            {scenario.title} · <span className="text-text-muted">{scenario.partnerName}</span>
          </p>
        </div>

        {/* ========================================================================= */}
        {/* TIER 2: SUMMARY METRIC STRIP (Decision IA - 5-10s nhận diện vấn đề)       */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-2 gap-2 rounded-control border border-border/80 bg-bg/60 p-3 sm:grid-cols-3 xl:grid-cols-6">
          {scenario.metrics.map((m) => (
            <div key={m.id} className="min-w-0">
              <span className="text-label text-text-muted">{m.label}</span>
              <div
                className={cn(
                  "mt-0.5 truncate font-mono text-body font-bold tabular-nums",
                  m.tone === "primary" && "text-primary",
                  m.tone === "success" && "text-success-fg",
                  m.tone === "warning" && "text-warning-fg",
                  m.tone === "info" && "text-info-fg",
                  m.tone === "neutral" && "text-text"
                )}
              >
                {m.value}
              </div>
              {m.hint ? (
                <p className="m-0 mt-0.5 truncate text-caption text-text-muted">
                  {m.hint}
                </p>
              ) : null}
            </div>
          ))}
        </div>

        {/* ========================================================================= */}
        {/* TIER 3: JOURNEY STEPPER (Tiến trình thực hiện)                           */}
        {/* ========================================================================= */}
        <div className="pt-2.5 pb-1 border-t border-border/60">
          <StatusStepper
            steps={scenario.journeySteps.map((step) => ({
              id: step.id,
              label: step.label,
              state:
                step.status === "completed"
                  ? "done"
                  : step.status === "current"
                    ? "current"
                    : "upcoming",
              time: step.time,
            }))}
            density="compact"
            aria-label="Tiến trình thực hiện"
          />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TABS NAVIGATION BAR USING CORE TABS                                       */}
      {/* ========================================================================= */}
      <Tabs
        items={tabs.map((t) => ({ value: t.id, label: t.label }))}
        value={activeTab}
        onValueChange={(val) => setActiveTab(val as any)}
      />

      {/* ========================================================================= */}
      {/* TAB CONTENT: 8/12 MAIN CONTENT + 4/12 ATTENTION SIDEBAR                  */}
      {/* ========================================================================= */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          {/* Main 8/12 */}
          <div className="space-y-4 xl:col-span-8">
            {/* Block 1: Dòng hàng tóm tắt (Khối Bảng Phụ Lồng Ghép - Nested Related Table) */}
            <RelatedEntityTable<TestUiLineItem>
              icon={<Layers className="size-4" />}
              headerDivider
              title="Dòng hàng & Sản lượng kế hoạch"
              headerEnd={
                <span className="text-caption text-text-muted">
                  {scenario.lines.length} mặt hàng đăng ký
                </span>
              }
              rows={scenario.lines}
              getRowId={(l) => l.id}
              showRowNumbers
              minWidthClassName="min-w-[720px]"
              columns={[
                {
                  id: "kcs",
                  header: "Kiểm nghiệm KCS",
                  align: "center",
                  cell: (l) => (
                    <Badge
                      tone={
                        l.qcStatus === "Passed"
                          ? "success"
                          : l.qcStatus === "Hold"
                          ? "danger"
                          : "warning"
                      }
                      label={
                        l.qcStatus === "Passed"
                          ? "Đạt kiểm nghiệm"
                          : l.qcStatus === "Hold"
                          ? "Tạm giữ QA"
                          : "Chờ kiểm nghiệm"
                      }
                    />
                  ),
                },
                {
                  id: "product",
                  header: "Tên sản phẩm",
                  minWidth: "180px",
                  maxWidth: "280px",
                  cell: (l) => (
                    <div className="min-w-0 max-w-full">
                      <div className="font-medium text-text truncate" title={l.itemName}>
                        {l.itemName}
                      </div>
                      <span className="font-mono text-caption text-text-muted truncate block">
                        {l.itemCode}
                      </span>
                    </div>
                  ),
                },
                {
                  id: "spec",
                  header: "Quy cách đóng gói",
                  minWidth: "140px",
                  maxWidth: "220px",
                  cell: (l) => (
                    <span
                      className="inline-block max-w-full truncate text-text-secondary"
                      title={l.spec}
                    >
                      {l.spec}
                    </span>
                  ),
                },
                {
                  id: "plannedQty",
                  header: "Kế hoạch",
                  align: "right",
                  cell: (l) => (
                    <span className="font-mono font-bold text-text tabular-nums">
                      {l.plannedQty.toLocaleString("vi-VN")} {l.uom}
                    </span>
                  ),
                },
                {
                  id: "actualQty",
                  header: "Thực tế đạt",
                  align: "right",
                  cell: (l) => (
                    <span className="font-mono font-bold text-primary tabular-nums">
                      {l.actualQty.toLocaleString("vi-VN")} {l.uom}
                    </span>
                  ),
                },
              ]}
            />

            {/* Block 2: Field Grid 2 cột với row dividers */}
            <DetailSectionCard
              dense
              headerDivider
              title="Thông số Kỹ thuật & Điều kiện bảo quản Lô"
              icon={<Thermometer className="size-4 text-info-fg" />}
            >
              <DetailFieldGrid columns={2}>
                <DetailField label="Nhiệt độ cấp đông IQF">
                  <span className="font-mono font-bold text-info-fg">-38.5°C (Đạt ngưỡng an toàn)</span>
                </DetailField>
                <DetailField label="Nhiệt độ tâm sản phẩm xuất xưởng">
                  <span className="font-mono font-bold text-info-fg">≤ -18.0°C</span>
                </DetailField>
                <DetailField label="Tỷ lệ mạ băng kiểm tra">
                  <span>10.2% (Định mức cho phép: 10% ± 0.5%)</span>
                </DetailField>
                <DetailField label="Tiêu chuẩn An toàn thực phẩm">
                  <div className="font-medium text-success-fg flex items-center gap-1.5">
                    <ShieldCheck className="size-4" />
                    <span>HACCP Codex / BRC Food Safety v9</span>
                  </div>
                </DetailField>
                <DetailField label="Kiểm tra kim loại CCP-2">
                  <span>Fe 1.2mm / Non-Fe 1.5mm / SUS 2.0mm: Đạt</span>
                </DetailField>
                <DetailField label="Số phiếu kiểm nghiệm Lab">
                  <span className="font-mono text-primary font-semibold">LAB-2026-F441 (Âm tính Salmonella)</span>
                </DetailField>
              </DetailFieldGrid>
            </DetailSectionCard>
          </div>

          {/* Sidebar 4/12: Attention & Supporting cards */}
          <div className="space-y-4 xl:col-span-4">
            {/* Attention Panel */}
            <div className="rounded-control border border-warning/40 bg-warning-soft/30 p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-warning-fg font-bold text-body">
                <AlertTriangle className="size-4" />
                <span>Thông Tin & Cảnh Báo ({scenario.attentionItems.length})</span>
              </div>

              <div className="space-y-2.5">
                {scenario.attentionItems.map((att) => (
                  <div
                    key={att.id}
                    className="rounded-control border border-warning/50 bg-surface p-3 text-caption space-y-1.5"
                  >
                    <div className="font-bold text-text">{att.title}</div>
                    <p className="m-0 text-text-secondary">{att.detail}</p>
                    {att.actionLabel && (
                      <Button size="sm" variant="soft" className="h-7 text-caption font-semibold mt-1">
                        {att.actionLabel} →
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Supporting Card: Đối tác & Phân xưởng */}
            <DetailSectionCard
              dense
              headerDivider
              title="Nhà Cung Cấp & Cơ sở"
              icon={<Warehouse className="size-4 text-primary" />}
            >
              <DetailFieldGrid columns={1}>
                <DetailField label="Nhà cung cấp NVL:">
                  <div>
                    <div className="font-semibold text-text">{scenario.partnerName}</div>
                    <span className="font-mono text-caption text-text-muted">{scenario.partnerCode}</span>
                  </div>
                </DetailField>
                <DetailField label="Địa điểm chế biến:">
                  <div className="font-medium text-text">{scenario.plant}</div>
                </DetailField>
                <DetailField label="Ca sản xuất:">
                  <div className="font-medium text-text">{scenario.shift}</div>
                </DetailField>
              </DetailFieldGrid>
            </DetailSectionCard>

            {/* Supporting Card: Trưởng ca phụ trách */}
            <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-2 text-caption">
              <h4 className="m-0 text-body font-bold text-text flex items-center gap-2">
                <UserCheck className="size-4 text-primary" />
                <span>Nhân Sự Phụ Trách Ca</span>
              </h4>
              <div className="flex items-center gap-2.5 pt-1">
                <div className="flex size-9 items-center justify-center rounded-full bg-primary-soft text-primary font-bold">
                  NV
                </div>
                <div>
                  <div className="font-bold text-text">Nguyễn Văn Hùng</div>
                  <div className="text-text-muted">Quản đốc phân xưởng · 0918.234.567</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Lines Tab */}
      {activeTab === "lines" && (
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs">
          <p className="text-text-secondary text-body">
            Chi tiết toàn bộ định mức nguyên vật liệu, phụ gia mạ băng, bao bì thùng carton cho lô {scenario.code}.
          </p>
        </div>
      )}

      {/* TAB CONTENT: CCP Logs Tab */}
      {activeTab === "ccp" && (
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
          <h3 className="m-0 text-h3 font-bold text-text">Nhật ký giám sát điểm CCP trực tiếp theo giờ</h3>
          <p className="text-text-secondary text-caption">
            Ghi nhận định kỳ 30 phút/lần bởi nhân viên QA trực chuyền cấp đông.
          </p>
        </div>
      )}

      {/* TAB CONTENT: Docs Tab */}
      {activeTab === "docs" && (
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs">
          <p className="text-text-secondary text-body">
            Hồ sơ kiểm dịch thủy sản vùng nuôi, chứng chỉ BRC/HACCP đính kèm theo lô hàng.
          </p>
        </div>
      )}

      {/* TAB CONTENT: Audit Tab */}
      {activeTab === "audit" && (
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-2">
          <h3 className="m-0 text-h3 font-bold text-text">Lịch sử thao tác & Phê duyệt</h3>
          <p className="text-caption text-text-muted">
            06:15 Lập phiếu bởi Nguyễn Văn Hùng · 07:00 KCS duyệt cá sống bởi Trần Thị Mai.
          </p>
        </div>
      )}
    </section>
  );
}
